import { fromZonedTime, toZonedTime } from "date-fns-tz";

import type { MLTripSummary } from "../types";

/** Civil calendar date (month is 0-based, same as `Date`). */
export type CalendarDay = {
  year: number;
  month: number;
  day: number;
};

export function getCalendarDayInTimezone(
  date: Date,
  tzKey: string,
): CalendarDay {
  const zoned = toZonedTime(date, tzKey);
  return {
    year: zoned.getFullYear(),
    month: zoned.getMonth(),
    day: zoned.getDate(),
  };
}

export function getCalendarDayBoundsFromParts(
  day: CalendarDay,
  tzKey: string,
): { startMs: number; endMs: number } {
  const zoned = toZonedTime(new Date(), tzKey);
  zoned.setFullYear(day.year, day.month, day.day);
  zoned.setHours(0, 0, 0, 0);
  const startLocal = new Date(zoned);
  const endLocal = new Date(zoned);
  endLocal.setHours(23, 59, 59, 999);

  return {
    startMs: fromZonedTime(startLocal, tzKey).getTime(),
    endMs: fromZonedTime(endLocal, tzKey).getTime(),
  };
}

/** UTC instant at start of `day` in `tzKey` (for pickers and date-only labels). */
export function calendarDayToDateInTimezone(
  day: CalendarDay,
  tzKey: string,
): Date {
  const { startMs } = getCalendarDayBoundsFromParts(day, tzKey);
  return new Date(startMs);
}

export function getCalendarDayBoundsUtc(
  date: Date,
  tzKey: string,
): { startMs: number; endMs: number } {
  return getCalendarDayBoundsFromParts(
    getCalendarDayInTimezone(date, tzKey),
    tzKey,
  );
}

/** Whether the trip overlaps `activeDay` interpreted in `tzKey`. */
export function isTripActiveOnCalendarDay(
  trip: MLTripSummary,
  activeDay: CalendarDay,
  tzKey: string,
): boolean {
  const tripStart = new Date(trip.startTime).getTime();
  if (Number.isNaN(tripStart)) {
    return false;
  }

  const tripEnd = trip.endTime
    ? new Date(trip.endTime).getTime()
    : Number.POSITIVE_INFINITY;
  if (Number.isNaN(tripEnd)) {
    return false;
  }

  const { startMs, endMs } = getCalendarDayBoundsFromParts(activeDay, tzKey);
  return tripStart <= endMs && tripEnd >= startMs;
}
